import { TService } from '@/models/services';
import { IServiceCellConfig } from '@/pages/commercial-proposals/blocks/cells/ServiceNameCell/ServiceNameCellSettings';
import { IPriceCellConfig } from '@/pages/commercial-proposals/blocks/cells/ServicePriceCell/ServicePriceCellSettings';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

function arrayMove<T>(array: T[], from: number, to: number): T[] {
  const newArray = [...array];
  const [removed] = newArray.splice(from, 1);
  newArray.splice(to, 0, removed);
  return newArray;
}

interface BlockStateActions {
  select: (uuid: string | null) => void;
  add: (block: IBlock, index?: number) => void;
  remove: (uuid: string) => void;
  reorder: (sourceIndex: number, destinationIndex: number) => void;
  update: <T extends IBlock>(block: T, replace?: boolean) => void;
  initialize: (blocks: IBlock[]) => void;
  clear: () => void;
}

interface CellState {
  selectedCell: string | null;
  actions: CellStateActions;
}

interface CellStateActions {
  select: (uuid: string | null) => void;
}

export const useCellStore = create<CellState>((set) => ({
  selectedCell: null,
  actions: {
    select: (uuid: string | null) => set({ selectedCell: uuid }),
  },
}));

export const useSelectedCell = () =>
  useCellStore((state) => state.selectedCell);

export const useCellActions = () => useCellStore((state) => state.actions);

interface BlocksState {
  selectedId: string | null;
  blocks: IBlock[];
  actions: BlockStateActions;
}

export const useBlocksStore = create<BlocksState>((set) => ({
  selectedId: null,
  blocks: [],
  actions: {
    update: <T extends IBlock>(block: T) =>
      set((state) => {
        const blocks = [...state.blocks];
        const index = blocks.findIndex((b) => b.uuid === block.uuid);
        if (index !== -1) {
          blocks[index] = { ...blocks[index], ...block };
        }
        return { blocks };
      }),
    select: (uuid: string | null) => set({ selectedId: uuid }),
    add: (block: IBlock) =>
      set((state) => ({
        blocks: [...state.blocks, block],
      })),
    remove: (uuid: string) =>
      set((state) => {
        const blocks = state.blocks.filter((b) => b.uuid !== uuid);
        const selectedId = state.selectedId === uuid ? null : state.selectedId;
        return { blocks, selectedId };
      }),
    reorder: (sourceIndex: number, destinationIndex: number) =>
      set((state) => {
        const blocks = arrayMove(state.blocks, sourceIndex, destinationIndex);
        blocks.forEach((b, index) => (b.weight = index));
        return { blocks };
      }),
    initialize: (blocks: IBlock[]) => set({ blocks }),
    clear: () => set({ blocks: [], selectedId: null }),
  },
}));

export const useSelectedBlock = () =>
  useBlocksStore((state) => state.selectedId);

export const useBlocksActions = () => useBlocksStore((state) => state.actions);

export const useBlockStore = create<{
  block: IBlock | null;
  setBlock: (block: IBlock | null) => void;
}>((set) => ({
  block: null,
  setBlock: (block: IBlock | null) => set({ block }),
}));

export enum BlockTypes {
  Services = 'services',
  ServicesInOnePackage = 'services-1',
  ServicesInThreePackages = 'services-3',
  ServicesInTwoPackages = 'services-2',
  Text = 'text',
  TitlePage = 'title-page',
}

export const getServiceBlockTiers = (type: BlockTypes) => {
  switch (type) {
    case BlockTypes.Services:
    case BlockTypes.ServicesInOnePackage:
      return 1;
    case BlockTypes.ServicesInTwoPackages:
      return 2;
    case BlockTypes.ServicesInThreePackages:
      return 3;
    default:
      throw new Error(`Unknown block type: ${type}`);
  }
};

export const isPackageBlock = (type: BlockTypes | string) =>
  type === BlockTypes.ServicesInOnePackage ||
  type === BlockTypes.ServicesInTwoPackages ||
  type === BlockTypes.ServicesInThreePackages;

export interface IBlock<
  T extends BlockTypes = BlockTypes,
  U = IServiceBlockConfig | ITitlePageBlockConfig,
> {
  cpId: number;
  uuid: string;
  title: string;
  type: T;
  weight: number;
  config: U;
}

export type ITitlePageBlock = IBlock<
  BlockTypes.TitlePage,
  ITitlePageBlockConfig
>;

export interface ITextBlock extends IBlock {
  text: string;
}

export interface IServiceBlockHeadingCell {
  title: string;
  showSubtitle: boolean;
  subtitle: string;
}

export interface IServiceBlockConfig {
  showHeadingRow: boolean;
  showSummaryRow: boolean;
  title: string;
  nonInteractive: boolean;
}

export interface ITitlePageBlockConfig {
  jurisdiction: string;
  documentTitle: string;
  subtitle: string;
  list: string[];
}

export interface IServiceBlock extends IBlock<BlockTypes, IServiceBlockConfig> {
  services: {
    blockUuid: string;
    serviceId: number;
    weight: number;
    config: IServiceCellConfig & { [index: string]: IPriceCellConfig };
    price: {
      amount: number;
      currency: string;
    };
    parentId?: number;
    /**
     * Minimal service metadata carried by the block itself, so rendering a row does
     * not cost a request per service. `id`/`name` are null when the service has since
     * been archived — render a fallback label rather than assuming it resolves.
     */
    record?: { id: number | null; name: string | null };
  }[];
}

export interface IServiceBlockService extends TService {
  included: boolean;
  readonly price: number;
  showDescription: boolean;
  showChildServices: boolean;
}

export const initializeNewBlock = (
  commercialProposalId: number,
  type: BlockTypes,
  weight: number,
) => {
  const uuid = uuidv4();
  const base = {
    cpId: commercialProposalId,
    uuid,
    weight,
    type,
    title: type.charAt(0).toUpperCase() + type.slice(1),
    config: {},
  } as Partial<IBlock>;

  switch (type) {
    case BlockTypes.TitlePage:
      return {
        ...base,
        title: 'Title Page',
        config: { jurisdiction: '', documentTitle: '', subtitle: '', list: [] },
      } as ITitlePageBlock;
    case BlockTypes.Text:
      return { ...base, text: '' } as ITextBlock;
    case BlockTypes.Services:
    case BlockTypes.ServicesInOnePackage:
    case BlockTypes.ServicesInTwoPackages:
    case BlockTypes.ServicesInThreePackages:
      return {
        ...base,
        services: [],
        config: {
          showHeadingRow: true,
          showSummaryRow: true,
          title: '',
          nonInteractive: false,
          heading: Array.from(
            { length: getServiceBlockTiers(type) + 1 },
            (_, i) => ({
              title:
                i === 0
                  ? 'Service Name'
                  : type === BlockTypes.ServicesInOnePackage
                    ? 'Package'
                    : `Package ${i}`,
              subtitle: '',
            }),
          ),
        },
      } as IServiceBlock;
    default:
      throw new Error(`Unknown block type: ${type}`);
  }
};
